import React, { useMemo } from "react";
import FormControl from "@mui/material/FormControl";
import InputWrapper from "./InputWrapper";
import ReadOnlyInput from "./ReadOnlyInput";
import { Controller } from "react-hook-form";
import { Autocomplete, FormHelperText, TextField, createFilterOptions } from "@mui/material";
import { DEFAULT_SIZE } from "../../../utils/constant";
import PropTypes from "prop-types";
import { sortOptions } from "../../../utils/formHelper";

export default function ProMultiSelectSelectField({
  name,
  mode,
  label,
  rules,
  control,
  isAddMode,
  infoMessage,
  options = [],
  size = DEFAULT_SIZE,
  startAdornment = null,
  withInfoIcon = false,
  fieldProps = {},
  hideSelected = false,
  allOptionValue = undefined,
  ...rest
}) {
  const sortedOptions = useMemo(() => sortOptions(options), [options]);
  const defaultFilter = useMemo(() => createFilterOptions(), []);

  const { canView = false, canAdd = false, canEdit = false } = rest.permission || {};
  if (!canView || (isAddMode && !canAdd)) return null;
  const editModeAllowed = isAddMode ? canAdd : canEdit;
  if (mode === "read" || (mode === "edit" && !editModeAllowed))
    return <ReadOnlyInput type="multiSelect" size={size} label={label} name={name} {...rest} rules={rules} />;

  return (
    <InputWrapper size={size}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <FormControl className="proInputWithMessage" fullWidth>
            <Autocomplete
              multiple
              options={sortedOptions}
              value={value || []}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              filterOptions={(options, state) => {
                const base = hideSelected
                  ? options.filter(opt => !(value || []).some(v => v?.value === opt?.value))
                  : options;
                return defaultFilter(base, state);
              }}
              onChange={(e, newValue) => {
                if (!allOptionValue) return onChange(newValue);
                const hasAll = newValue.some(v => v?.value === allOptionValue);
                const hadAll = (value || []).some(v => v?.value === allOptionValue);
                if (hasAll && !hadAll) onChange(newValue.filter(v => v?.value === allOptionValue));
                else if (hasAll && newValue.length > 1) onChange(newValue.filter(v => v?.value !== allOptionValue));
                else onChange(newValue);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={
                    <span>
                      {label}
                      {rules?.required && <span style={{ color: "red" }}> *</span>}
                    </span>
                  }
                  InputLabelProps={{ shrink: true }}
                  placeholder={value?.length ? "" : label}
                  variant="outlined"
                  fullWidth
                />
              )}
              {...fieldProps}
            />
            <FormHelperText error={!!error}>{error?.message}</FormHelperText>
          </FormControl>
        )}
      />
    </InputWrapper>
  );
}

ProMultiSelectSelectField.propTypes = {
  name: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["read", "edit"]).isRequired,
  label: PropTypes.string.isRequired,
  rules: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  isAddMode: PropTypes.bool,
  withInfoIcon: PropTypes.bool,
  size: PropTypes.string,
  infoMessage: PropTypes.string,
  startAdornment: PropTypes.node,
  allOptionValue: PropTypes.string,
  hideSelected: PropTypes.bool,
  fieldProps: PropTypes.object,
  options: PropTypes.array,
  permission: PropTypes.shape({
    canView: PropTypes.bool,
    canAdd: PropTypes.bool,
    canEdit: PropTypes.bool
  })
};
